import { observer } from "mobx-react";
import { IAppliedFilterChipComponent } from "../BaseChipComponents/IAppliedFilterChipComponent";
import { useStore } from "utils/useStore";
import AppliedFilterChip from "../BaseChipComponents/AppliedFilterChipBase";
import React from "react";
import { Stack } from "@mui/material";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import {
    formatModuleScoreConditions,
    groupModuleScores,
} from "./moduleScoreLabels";
import { AuthStore } from "stores/AuthStore";
import { useChipFiltersStore } from "../BaseChipComponents/chipUtils";

export const ModuleScoresChips = observer(
    (props: IAppliedFilterChipComponent) => {
        const applicationFiltersStore = useChipFiltersStore(props);
        const valuesStore = useStore(ApplicationFilterValuesStore);
        const authStore = useStore(AuthStore);

        if (!authStore.canUserView("Module Scores")) {
            return null;
        }
        if (
            authStore.canUserView("Restrict Evaluations") &&
            !authStore.isUserUltra()
        ) {
            return null;
        }
        if (
            !applicationFiltersStore.moduleScores ||
            applicationFiltersStore.moduleScores.length === 0
        ) {
            return null;
        }

        // One chip per module rather than per condition. A module's conditions are ANDed and are
        // added and removed together in the drawer, so splitting them would repeat the module name
        // and hide the fact that they describe one restriction.
        const groups = groupModuleScores(applicationFiltersStore.moduleScores);

        const isModulesLoading = valuesStore.getTaskLoading(ApplicationFilterValuesStore.Tasks.LOAD_LICENSED_HIERARCHY_MODULES);

        if (isModulesLoading) {
            return groups.map((group) => (
                <AppliedFilterChip
                    key={group.licensedModuleId}
                    disabled={props.disabled}
                    isApplicable={props.isApplicable}
                >
                    <Stack direction="row" flexWrap="nowrap" alignItems="center" spacing={1}>
                        <span>Module Score:</span>
                        <AcxLoadingIndicator size={16} alternate="PuffLoader" />
                    </Stack>
                </AppliedFilterChip>
            ));
        }

        return groups.map((group) => {
            const moduleTitle =
                valuesStore.hierarchyLicensedModules?.find(
                    (x) => x.id === group.licensedModuleId,
                )?.title ?? group.licensedModuleId;
            const conditions = formatModuleScoreConditions(
                group.conditions.map((condition) => condition.entry),
            );

            return (
                <AppliedFilterChip
                    key={group.licensedModuleId}
                    disabled={props.disabled}
                    isApplicable={props.isApplicable}
                    onRemove={() =>
                        applicationFiltersStore.setModuleScores(
                            (applicationFiltersStore.moduleScores ?? []).filter(
                                (x) =>
                                    x.licensedModuleId !==
                                    group.licensedModuleId,
                            ),
                        )
                    }
                >
                    {moduleTitle} Score {conditions}
                </AppliedFilterChip>
            );
        });
    },
);
